import { useEffect, useState, useCallback, useRef } from 'react';
import dayjs from 'dayjs';

import Pagination from '../../components/reusable/pagination/pagination.component';
import Spinner from '../../components/reusable/spinner/spinner.component';
import TableControls from '../../components/reusable/table-controls/table-controls.component';
import UsersTable from '../../components/reusable/tables/users-table/users-table.component';

import useTableState from '../../hooks/useTableState';
import Client from '../../tools/client';

import {
    TabContainer,
    TabSelector
} from './accounts.styles';

import {
    ContentContainer,
    MainContainer,
    MainTitle
} from '../../styles/page.styles';

const client = new Client();

const Accounts = () => {
    const [loading, setLoading] = useState(true);
    const [accounts, setAccounts] = useState(null);
    const [accountsCount, setAccountsCount] = useState(null);

    const {
        page,
        size,
        search,
        sortKey,
        sortDirection,
        setPage,
        setSize,
        setSearch,
        setSortKey,
        setSortDirection
    } = useTableState({ defaultSize: 25 });

    const isInitialMount = useRef(true);

    const getAccounts = useCallback(async () => {
        setLoading(true);
        let query = `?page=${page}&size=${size}`;
        if (search) query += `&search=${encodeURIComponent(search)}`;
        if (sortDirection) query += `&sortDirection=${sortDirection}`;
        if (sortKey) query += `&sortKey=${sortKey}`;
        const res = await client.getUsers(query);
        setAccounts(res.rows);
        setAccountsCount(res.count);
        setLoading(false);
    }, [page, size, search, sortKey, sortDirection]);

    useEffect(() => {
        getAccounts();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (isInitialMount.current) {
            isInitialMount.current = false;
            return;
        }
        getAccounts();
    }, [page, size, search, sortKey, sortDirection, getAccounts]);

    const handleSearchSubmit = useCallback(() => {}, []);

    // Export configuration
    const exportFn = async () => {
        const res = await client.exportUsers();
        return res.rows || [];
    };

    const formatExportData = (data) => {
        return data.map((user) => {
            const joinDate = dayjs(user.createdAt).format('MM/DD/YYYY');
            return [
                user.id || '',
                user.email || '',
                user.username || '',
                user.firstName || '',
                user.lastName || '',
                user.phone || '',
                joinDate
            ];
        });
    };

    const exportHeaders = ['ID', 'Email', 'Username', 'First Name', 'Last Name', 'Phone', 'Join Date'];

    return (
        <MainContainer>
            <TabContainer>
                <TabSelector active={true}>Users</TabSelector>
            </TabContainer>

            <ContentContainer>
                <MainTitle>Users</MainTitle>

                <TableControls
                    search={search}
                    setSearch={setSearch}
                    onSearchSubmit={handleSearchSubmit}
                    size={size}
                    setSize={setSize}
                    entityName="users"
                    exportFn={exportFn}
                    formatExportData={formatExportData}
                    exportHeaders={exportHeaders}
                />

                {loading ? (
                    <Spinner />
                ) : (
                    <>
                        <UsersTable
                            users={accounts}
                            sortKey={sortKey}
                            setSortKey={setSortKey}
                            sortDirection={sortDirection}
                            setSortDirection={setSortDirection}
                        />

                        <Pagination
                            count={accountsCount}
                            size={size}
                            page={page}
                            changePage={setPage}
                        />
                    </>
                )}
            </ContentContainer>
        </MainContainer>
    );
};

export default Accounts;
